import PeopleAltRoundedIcon from '@mui/icons-material/PeopleAltRounded';
import PersonAddAlt1RoundedIcon from '@mui/icons-material/PersonAddAlt1Rounded';
import PersonRemoveRoundedIcon from '@mui/icons-material/PersonRemoveRounded';
import {
  Autocomplete,
  Button,
  IconButton,
  Sheet,
  Stack,
  Table,
  Typography,
} from '@mui/joy';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import useEvent from '../../hooks/useEvent';
import { useUser } from '../../hooks/useUser';
import useUsers from '../../hooks/useUsers';
import {
  inviteForEventService,
  removeInviteForEventService,
} from '../../redux/services/eventsService';
import ExportAttendeesButton from './downloadAttendees';
import ClearRoundedIcon from '@mui/icons-material/ClearRounded';

export type EventDetailContentRightProps = {
  eventId: string;
};

export const EventDetailContentRight = (
  props: EventDetailContentRightProps
) => {
  const { eventId } = props;
  const eventQuery = useEvent(eventId as string);
  const [numberOfAttendeesToShow, setNumberOfAttendeesToShow] = useState(10);
  const [loading, setLoading] = useState(false);
  const [userToInvite, setUserToInvite] = useState<string | null>(null);
  const usersQuery = useUsers();
  const userQuery = useUser();
  const { t } = useTranslation();

  const inviteUser = async () => {
    try {
      if (!userToInvite) {
        return;
      }
      setLoading(true);
      await inviteForEventService(eventId, userToInvite);
      await eventQuery.refetch();
      setLoading(false);
    } catch (error) {
    } finally {
      setLoading(false);
    }
  };

  const options = useMemo(() => {
    if (!usersQuery.isSuccess) return [];
    if (!eventQuery.isSuccess) return [];
    return (usersQuery.data || [])
      .filter((user: any) => {
        const { branch: topicBranch } = eventQuery.data;
        return user.branch === topicBranch || topicBranch === 'Public';
      })
      .map((e: any) => {
        return {
          key: e._id,
          label: `${e.firstName} ${e.lastName}`,
        };
      });
  }, [usersQuery, eventQuery]);

  const isAdmin = useMemo(() => {
    return (
      userQuery.data?.role === 'ADMIN' || userQuery.data?.role === 'SUPER_ADMIN'
    );
  }, [userQuery]);

  type attendeesInfo = { total: number; attendeesToShow: Array<any> };
  const { total, attendeesToShow }: attendeesInfo = useMemo(() => {
    if (!eventQuery.isSuccess)
      return {
        total: 0,
        attendeesToShow: [],
      };
    return {
      total: eventQuery.data.attendees.length,
      attendeesToShow: eventQuery.data.attendees.slice(
        0,
        numberOfAttendeesToShow
      ),
    };
  }, [eventQuery, numberOfAttendeesToShow]);

  const removeUser = async (id: string) => {
    setLoading(true);
    try {
      await removeInviteForEventService(eventId, id);
      eventQuery.refetch();
      userQuery.refetch();
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  if (!eventQuery.isSuccess || !usersQuery.isSuccess || !userQuery.isSuccess)
    return null;

  const isFull =
    eventQuery.data.attendees.length >= eventQuery.data.maxAttendees &&
    eventQuery.data.maxAttendees != -1;

  if (!isAdmin) return null;

  return (
    <Stack spacing={1}>
      {/* Admin download button */}
      <ExportAttendeesButton />

      <Sheet variant="outlined" sx={{ borderRadius: 'sm', padding: '1rem' }}>
        {/* Header */}
        <Stack direction={'row'} justifyContent={'space-between'}>
          {eventQuery.data.maxAttendees === -1 ? (
            <Typography>Deelnemers</Typography>
          ) : (
            <Typography>{`Deelnemers ${eventQuery.data.attendees.length} / ${eventQuery.data.maxAttendees}`}</Typography>
          )}
          <PeopleAltRoundedIcon className="w-5" />
        </Stack>

        {/* Members */}
        <Table>
          <thead>
            <tr>
              <th>Naam</th>
              <th style={{ width: '4rem' }}></th>
            </tr>
          </thead>
          {attendeesToShow.map((member) => {
            return (
              <tr key={member._id}>
                <th>
                  {member.firstName} {member.lastName}
                </th>
                <th style={{ float: 'right' }}>
                  {
                    <IconButton
                      color="neutral"
                      onClick={async (e) => {
                        await removeUser(member._id);
                      }}
                      disabled={loading}
                    >
                      <ClearRoundedIcon />
                    </IconButton>
                  }
                </th>
              </tr>
            );
          })}
        </Table>

        {/* Show more button */}
        {eventQuery.data.attendees.length > 10 && (
          <Button
            onClick={() =>
              setNumberOfAttendeesToShow((curr) => (curr > 10 ? 10 : total))
            }
          >
            {attendeesToShow.length > 10
              ? t('posts.show_less')
              : `${t('posts.show_more')} (${eventQuery.data.attendees.length})`}
          </Button>
        )}
      </Sheet>

      {/* Dropdown for adding a member */}
      <Stack direction={'row'} spacing={1}>
        <Autocomplete
          options={options}
          onChange={(_, newValue) => {
            if (!newValue) {
              setUserToInvite(null);
              return;
            }
            setUserToInvite(newValue.key);
          }}
          placeholder="Selecteer een deelnemer"
        />
        <Button
          onClick={inviteUser}
          disabled={isFull || loading}
          startDecorator={<PersonAddAlt1RoundedIcon />}
        >
          Toevoegen
        </Button>
      </Stack>
    </Stack>
  );
};
